// Mock CV Data for Erika Morishita
export const cvData = {
  personal: {
    name: "ERIKA MORISHITA",
    title: "Complex ideas, compelling visuals",
    location: "Geneva, Switzerland",
    phone: "+41 078 726 76 38",
    email: "erikamorishita@gmail.com",
    linkedin: "https://www.linkedin.com/in/erika-morishita-sarmiento-b8631067/",
    summary: "Strategic Communications, Graphic Designer and Multimedia professional with over 13 years of experience. Specialized in transforming complex policy and technical data into compelling content to drive engagement across diverse global audiences. Expert in leading visual identity strategies, managing digital platforms, and executing end-to-end multimedia and graphic design production in fast-paced, mission-driven environments."
  },
  
  experience: [
    {
      id: 1,
      title: "Communications Associate",
      organization: "UNITED NATIONS HIGH COMMISSIONER FOR REFUGEES",
      duration: "Aug 2020 - Sep 2025",
      responsibilities: [
        "Led visual communications for UNHCR's Regional Bureau for Europe (RBE) for five years, supervising a graphic designer and directing a cross-functional team.",
        "Conceptualized and produced high-impact digital and print content—including reports, brand identity guidelines, emergency updates, and videos.",
        "Specialized in creating accessible, visually compelling content using Adobe Creative Cloud and Canva. Tracked digital outreach activities and engagement metrics."
      ]
    },
    {
      id: 2,
      title: "Multimedia and Communications Consultant",
      organization: "UNITED NATIONS CONFERENCE ON TRADE AND DEVELOPMENT",
      duration: "Jan 2018 - Apr 2020",
      responsibilities: [
        "Led the TrainForTrade re-branding strategy, transforming complex trade and development topics into clear, engaging content.",
        "Produced around 200 multimedia products including videos for interactive e-learning modules, driving significant expansion in global digital outreach.",
        "Co-designed and managed the program's website while providing technical and content support for social media and e-learning platforms."
      ]
    },
    {
      id: 3,
      title: "Media and Communications Consultant",
      organization: "NGOS AND REGIONAL ORGANIZATIONS",
      duration: "Jul 2013 - Jan 2017",
      responsibilities: [
        "Executed over 12 consultancies for NGOs and regional organizations, designing comprehensive communication strategies.",
        "Produced high-impact multimedia projects, including documentary series, podcasts, and photo essays.",
        "Led website development, social media guidelines, and fundraising campaigns for various organizations."
      ]
    }
  ],
  
  education: [
    {
      id: 1,
      degree: "Master in Media Practice for Development and Social Change",
      achievement: "Merit",
      institution: "University of Sussex",
      location: "Brighton, United Kingdom",
      duration: "2013 - 2014",
      award: "Media Practice for Development and Social Change Scholarship"
    },
    {
      id: 2,
      degree: "Master in International Conflicts Studies",
      institution: "King's College London",
      location: "London, United Kingdom",
      duration: "2008 - 2009"
    }
  ],
  
  skills: [
    { name: "Digital & Graphic Design", icon: "Palette" },
    { name: "Video Production", icon: "Video" },
    { name: "Photography", icon: "Camera" },
    { name: "Animation & Motion Graphics", icon: "Film" },
    { name: "Website Design", icon: "Globe" },
    { name: "UI/UX Design", icon: "Layout" },
    { name: "Digital Marketing", icon: "TrendingUp" },
    { name: "Data Visualization", icon: "BarChart3" },
    { name: "Desktop Publishing", icon: "FileText" },
    { name: "Infographics", icon: "PieChart" }
  ],
  
  software: [
    { name: "Adobe Illustrator", proficiency: 95 },
    { name: "Adobe InDesign", proficiency: 95 },
    { name: "Adobe Photoshop", proficiency: 90 },
    { name: "Adobe After Effects", proficiency: 85 },
    { name: "Adobe Premiere Pro", proficiency: 85 },
    { name: "Canva", proficiency: 90 },
    { name: "Figma", proficiency: 95 },
    { name: "WordPress", proficiency: 85 },
    { name: "Google Analytics", proficiency: 80 }
  ],
  
  languages: [
    { name: "English", level: "Fluent" },
    { name: "Spanish", level: "Native" },
    { name: "French", level: "Limited working proficiency" }
  ],
  
  whyHireMe: [
    "I love creating impactful content that connects and inspires, even on complex topics.",
    "I turn complex information into stories and beautiful designs—almost like magic.",
    "I work fast and always deliver on time.",
    "I pay attention to detail and deliver high-quality products.",
    "I bring over a decade of experience across content creation, graphic design, videography, and photography.",
    "I have produced thousands of products, including publications, reports, data visualizations, infographics, illustrations, branding materials, videos, and websites.",
    "I'm versatile; I design both for print and online versions.",
    "I'm proudly multicultural—born in Colombia, with the discipline of a Japanese father and the passion of an half Italian-Colombian mother."
  ],
  
  caseStudies: [
    {
      id: 1,
      title: "UNHCR Visual Leadership & Multimedia",
      organization: "United Nations High Commissioner for Refugees",
      category: "Visual Communications",
      description: "Led visual communications for UNHCR's Regional Bureau for Europe (RBE) for five years, managing visual identity across 30+ country offices and creating high-impact, culturally relevant content.",
      responsibilities: [
        "Supervised graphic designer and directed cross-functional teams",
        "Provided training in photography, graphic design, and web management to 30+ country offices",
        "Co-managed RBE website and newsletters as central hub for regional storytelling",
        "Produced diverse library of digital and print content including emergency updates, videos, and reports"
      ],
      achievements: [
        "Achieved brand alignment across entire European region",
        "Established high-impact digital presence standardizing UNHCR Europe communications",
        "Produced global UEFA Euro 2020 campaign video with 8-hour turnaround",
        "Collaborated on #IBelong campaign adapted for National TV broadcast",
        "Iconography: Standardized UNHCR icons to align with OCHA design styles",
        "Supported UNHCR UK's Goal Click campaign with design and video production"
      ],
      achievementLinks: {
        "Goal Click campaign": "https://www.unhcr.org/goalclick/goal-click-refugees/"
      },
      deliverables: [
        "Selected Stories and Highlights newsletter",
        "UEFA Euro 2020 global campaign video",
        "#IBelong campaign content for TV broadcast",
        "Training materials for 30+ country offices"
      ],
      impact: "Brand alignment achieved across region with standardized digital presence reaching global audiences",
      gallery: [
        "/case-studies/goal_click_campaign.png",
        "/case-studies/unhcr_visual_1.png",
        "/case-studies/unhcr_visual_2.png",
        "/case-studies/unhcr_asylum_campaign.jpg",
        "/case-studies/page3_img1.png",
        "/case-studies/page3_img2.png",
        "/case-studies/page3_img4.png",
        "/case-studies/page3_img5.png"
      ]
    },
    {
      id: 2,
      title: "UNHCR Data-Driven Content Strategy",
      organization: "United Nations High Commissioner for Refugees",
      category: "Graphic Design & Analytics",
      description: "Transformed UNHCR's content approach from simple posting to data-driven optimization for multimedia assets, ensuring humanitarian messages reached the widest possible audience.",
      responsibilities: [
        "Tracked digital outreach activities and engagement metrics across platforms",
        "Used data-driven insights to tailor content for diverse international audiences",
        "Created accessible, visually compelling content using Adobe Creative Cloud and Canva"
      ],
      achievements: [
        "Delivered data-driven recommendations that significantly improved cross-platform campaign effectiveness",
        "Ensured critical humanitarian messages reached widest possible audience",
        "Blue Dot Hubs: Created full brand guidelines and visibility materials for Ukraine emergency hubs",
        "Interagency Coordination: Developed visual identity and social assets for the regional refugee response plan for the Ukraine Situation"
      ],
      achievementLinks: {
        "Blue Dot Hubs": "https://vimeo.com/250946908"
      },
      deliverables: [
        "Ukraine: Putting People at the Centre - 30 Years of Partnership report",
        "Lives on Hold: Profiles and Intentions of Refugees from Ukraine",
        "Refugees and Migrants from South-West Asia to Europe report",
        "Cross-platform social media campaigns"
      ],
      impact: "Significantly improved impact and effectiveness of social media campaigns through data-driven approach",
      gallery: [
        "/case-studies/page4_img1.png",
        "/case-studies/unhcr_ukraine_report.png",
        "/case-studies/page4_img2.png",
        "/case-studies/unhcr_regional_overview.png",
        "/case-studies/unhcr_report_map.png"
      ]
    },
    {
      id: 3,
      title: "UNCTAD TrainForTrade Re-branding",
      organization: "UN Conference on Trade and Development",
      category: "E-Learning & Multimedia",
      description: "Translated complex trade topics and e-commerce frameworks into accessible digital formats for global audiences, creating cohesive visual identity and comprehensive multimedia library.",
      responsibilities: [
        "Co-developed specialized e-learning modules for e-Commerce, Digital Identity, and International Statistics",
        "Produced 200+ multimedia assets managing full lifecycle from filming to final editing",
        "Designed logo and developed comprehensive stationery kit and style guides",
        "Co-designed TrainForTrade website for improved user experience and accessibility"
      ],
      achievements: [
        "Successfully translated complex trade topics into accessible digital formats",
        "Created cohesive visual identity supporting international trade capacity building",
        "Drove significant expansion in global digital outreach and learner engagement"
      ],
      deliverables: [
        "3 specialized e-learning modules",
        "200+ multimedia assets (videos, interactive presentations)",
        "Complete brand identity package (logo, stationery, style guides)",
        "Redesigned TrainForTrade website"
      ],
      impact: "200+ multimedia products created, driving significant expansion in global digital outreach",
      gallery: [
        "/case-studies/page5_img1.png",
        "/case-studies/tft_new_1.png",
        "/case-studies/tft_new_2.png",
        "/case-studies/tft_new_3.png"
      ]
    },
    {
      id: 4,
      title: "PACER Plus Video Series",
      organization: "OCTA (Pacific Community)",
      category: "Documentary & Storytelling",
      description: "Transformed dense legal trade agreement text into compelling human-interest stories, demonstrating how PACER Plus creates tangible economic opportunities for Pacific Island communities.",
      responsibilities: [
        "Centered video series on human-interest stories to transform policy into personality",
        "Documented Ni-Vanuatu seasonal worker journey illustrating labor mobility benefits",
        "Showcased Samoa's taro industry resilience explaining role of trade agreements",
        "Conducted 30+ interviews across four countries breaking down technical barriers"
      ],
      achievements: [
        "Officially recognized by government leaders for clarity and impact",
        "Made regional integration benefits accessible from government leaders to village farmers",
        "Demonstrated tangible economic opportunities created by trade agreements"
      ],
      deliverables: [
        "PACER Plus documentary video series",
        "30+ interviews across 4 Pacific countries",
        "Human-interest storytelling content"
      ],
      impact: "Official government recognition for clarity and impact on local stakeholders across Pacific region",
      gallery: [
        "/case-studies/page6_img1.png",
        "/case-studies/page6_img2.png",
        "/case-studies/pacer_plus_trade.png",
        "/case-studies/page6_img6.png"
      ],
      videoPortfolioUrl: "https://vimeo.com/250946908"
    }
  ]
};
